/**
 * Deterministic alignment score for strategic options.
 * Calculates how well an option aligns with Phase 1 commitments.
 * Score: 0–100, higher = better aligned.
 */

export interface AlignmentScoreInput {
  is_constraint_break?: boolean;
  is_strategy_shift?: boolean;
  total_capacity_percent?: number; // baseline + option
  reversibility_level?: string;
  inaction_cost_effect?: string;
  commit_mode?: boolean;
  time_to_first_signal_days?: number;
  validation_deadline_days?: number;
}

export interface AlignmentScoreResult {
  score: number;
  deductions: string[];
  color: string; // tailwind color class
}

export function calculateAlignmentScore(input: AlignmentScoreInput): AlignmentScoreResult {
  let score = 100;
  const deductions: string[] = [];

  if (input.is_constraint_break) {
    score -= 40;
    deductions.push("Deadline-Bruch (−40)");
  }

  if (input.is_strategy_shift) {
    score -= 50;
    deductions.push("Strategiewechsel (−50)");
  }

  if (input.total_capacity_percent != null && input.total_capacity_percent > 80) {
    score -= 20;
    deductions.push("Kapazität >80% (−20)");
  }

  if (
    input.reversibility_level === "gering" &&
    input.is_constraint_break
  ) {
    score -= 20;
    deductions.push("Gering reversibel + Deadline-Bruch (−20)");
  }

  if (
    input.commit_mode &&
    input.inaction_cost_effect === "ignores"
  ) {
    score -= 10;
    deductions.push("Inaction-Kosten ignoriert (−10)");
  }

  // Deadline proximity penalty: signal takes >80% of deadline
  if (
    input.time_to_first_signal_days != null &&
    input.validation_deadline_days != null &&
    input.validation_deadline_days > 0 &&
    !input.is_constraint_break // already penalized above
  ) {
    const ratio = input.time_to_first_signal_days / input.validation_deadline_days;
    if (ratio > 0.8) {
      score -= 10;
      deductions.push("Signal nahe Deadline (−10)");
    }
  }

  score = Math.max(0, score);

  const color =
    score >= 80
      ? "text-emerald-600 dark:text-emerald-400"
      : score >= 50
        ? "text-amber-600 dark:text-amber-400"
        : "text-destructive";

  return { score, deductions, color };
}

export const INACTION_FACTORS: Record<string, number> = {
  eliminates: 0.2,
  reduces: 0.6,
  shifts: 0.8,
  ignores: 1.0,
};

export function calculateEffectiveExposure(
  baselineDailyExposure: number,
  inactionCostEffect?: string
): number {
  const factor = INACTION_FACTORS[inactionCostEffect || ""] ?? 1.0;
  return Math.round(baselineDailyExposure * factor);
}
